import { DOCUMENT } from '@angular/common';
import { Injectable, NgZone, inject } from '@angular/core';

/**
 * The element a closing overlay hands the focus to, either directly or resolved when the
 * restoration is carried out — for a target that is only rendered while the overlay closes.
 */
export type HisFocusRestoreTarget = HTMLElement | null | ( () => HTMLElement | null );

type OverlayEntry = {
    id: symbol;
    opener: HisFocusRestoreTarget;
};

/**
 * This service orchestrates the restoration of focus when overlays are closed. It maintains a stack of open overlays
 * and their associated opener elements. When an overlay is closed, it attempts to restore focus to the appropriate
 * opener or a fallback element based on the last user interaction.
 */
@Injectable( {
    providedIn: 'root'
} )
export class HisRestoreFocusOrchestratorService {
    private readonly document = inject( DOCUMENT );
    private readonly ngZone = inject( NgZone );
    private readonly overlayIds = new WeakMap<object, symbol>();
    private readonly stack: OverlayEntry[] = [];
    private readonly cleanupCallbacks: Array<() => void> = [];
    private lastInteractionTarget: HTMLElement | null = null;
    private pendingRestoreToken?: symbol;

    constructor() {
        this.ngZone.runOutsideAngular( () => {
            this.addDocumentCaptureListener( 'pointerdown', ( event: Event ) => {
                this.lastInteractionTarget = this.getElementFromTarget( event.target );
            } );
            this.addDocumentCaptureListener( 'keydown', ( event: Event ) => {
                const keyboardEvent = event as KeyboardEvent;
                if ( keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ' ) {
                    this.lastInteractionTarget = this.getElementFromTarget( event.target );
                }
            } );
        } );
    }

    registerOpen( overlayRef: object, openerOverride?: HTMLElement | null ): void {
        const id = this.getOverlayId( overlayRef );
        const opener = this.resolveOpener( openerOverride );

        this.removeFromStack( id );
        this.stack.push( {
            id,
            opener
        } );
    }

    registerClose( overlayRef: object ): void {
        const id = this.getOverlayId( overlayRef );
        const lastIndex = this.findStackIndex( id );
        if ( lastIndex < 0 ) {
            return;
        }

        const wasTopmost = lastIndex === this.stack.length - 1;
        const [entry] = this.stack.splice( lastIndex, 1 );

        if ( !wasTopmost ) {
            return;
        }

        this.scheduleRestore( entry.opener );
    }

    /**
     * Restores the focus to {@code target} instead of the element that opened {@code overlayRef}
     * — for an overlay whose result is meant to be worked on right away.
     *
     * Meant to be called while the overlay is still open, as its close is registered before the
     * result is rendered; a target that only comes into being with that result is therefore
     * passed as a function, which is called when the focus is actually restored. A restoration
     * that is already scheduled is retargeted as long as it has not been carried out yet.
     */
    restoreTo( overlayRef: object, target: HisFocusRestoreTarget ): void {
        const index = this.findStackIndex( this.getOverlayId( overlayRef ) );

        if ( index >= 0 ) {
            this.stack[index].opener = target;
            return;
        }

        if ( this.pendingRestoreToken != null ) {
            this.scheduleRestore( target );
        }
    }

    private scheduleRestore( preferredTarget: HisFocusRestoreTarget ): void {
        const token = Symbol( 'focusRestore' );
        this.pendingRestoreToken = token;

        this.ngZone.runOutsideAngular( () => {
            setTimeout( () => {
                if ( this.pendingRestoreToken !== token ) {
                    return;
                }
                this.pendingRestoreToken = undefined;
                const target = this.resolveRestorationTarget(
                    typeof preferredTarget === 'function' ? preferredTarget() : preferredTarget );
                if ( target ) {
                    target.focus( {
                        preventScroll: true
                    } );
                }
            }, 0 );
        } );
    }

    private resolveOpener( openerOverride?: HTMLElement | null ): HTMLElement | null {
        if ( this.isValidFocusTarget( openerOverride ) ) {
            return openerOverride as HTMLElement;
        }

        const activeElement = this.getElementFromTarget( this.document.activeElement );
        if ( this.isValidFocusTarget( activeElement ) ) {
            return activeElement as HTMLElement;
        }

        if ( this.isValidFocusTarget( this.lastInteractionTarget ) ) {
            return this.lastInteractionTarget;
        }

        return null;
    }

    private resolveRestorationTarget( preferredTarget: HTMLElement | null ): HTMLElement | null {
        if ( this.isValidFocusTarget( preferredTarget ) ) {
            return preferredTarget;
        }

        const fallbackFromInteraction = this.lastInteractionTarget;
        if ( this.isValidFocusTarget( fallbackFromInteraction ) ) {
            return fallbackFromInteraction;
        }

        const firstFocusable = this.document.querySelector(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) as HTMLElement | null;

        if ( this.isValidFocusTarget( firstFocusable ) ) {
            return firstFocusable;
        }

        return null;
    }

    private getOverlayId( overlayRef: object ): symbol {
        let id = this.overlayIds.get( overlayRef );
        if ( !id ) {
            id = Symbol( 'overlay' );
            this.overlayIds.set( overlayRef, id );
        }
        return id;
    }

    private findStackIndex( id: symbol ): number {
        for ( let i = this.stack.length - 1; i >= 0; i-- ) {
            if ( this.stack[i].id === id ) {
                return i;
            }
        }
        return -1;
    }

    private removeFromStack( id: symbol ): void {
        const index = this.findStackIndex( id );
        if ( index >= 0 ) {
            this.stack.splice( index, 1 );
        }
    }

    private addDocumentCaptureListener( type: string, listener: EventListener ): void {
        this.document.addEventListener( type, listener, true );
        this.cleanupCallbacks.push( () => this.document.removeEventListener( type, listener, true ) );
    }

    private getElementFromTarget( target: EventTarget | null ): HTMLElement | null {
        return target instanceof HTMLElement ? target : null;
    }

    private isValidFocusTarget( candidate: HTMLElement | null | undefined ): candidate is HTMLElement {
        if ( !candidate ) {
            return false;
        }
        if ( !candidate.isConnected ) {
            return false;
        }
        if ( candidate.closest( '[aria-hidden="true"]' ) ) {
            return false;
        }

        const asAny = candidate as any;
        if ( typeof asAny.disabled === 'boolean' && asAny.disabled ) {
            return false;
        }
        if ( candidate.getAttribute( 'aria-disabled' ) === 'true' ) {
            return false;
        }
        if ( candidate.getClientRects().length === 0 ) {
            return false;
        }

        return typeof candidate.focus === 'function';
    }
}
